import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { apiQueryAdapter } from "app/data/apiQueryAdapter";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import Loading from "ui-components/loading/loading";
import moment from "moment";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface TrendlineDataPoint {
  date: string;
  value: number;
}

interface TrendlineResponse {
  terminal_id: number;
  metric: string;
  period: string;
  start_date: string;
  end_date: string;
  data_points: number;
  trendline: TrendlineDataPoint[];
}

interface TrendlineModalProps {
  isOpen: boolean;
  onClose: () => void;
  terminalId: string;
  metric: string;
  metricLabel: string;
  secondaryMetric?: string;
  secondaryMetricLabel?: string;
  selectedDate?: string;
}

type Period = "1w" | "1m" | "6m" | "1y";

const fetchTrendline = async (terminalId: string, metric: string, period: Period): Promise<TrendlineResponse> => {
  const response = await apiQueryAdapter.query("/ai-analytics/eod/analytics/trendline", "GET", {
    params: {
      destinationTerminal: terminalId,
      metric: metric,
      period: period
    }
  });
  return response.data;
};

const TrendlineModal: React.FC<TrendlineModalProps> = ({ isOpen, onClose, terminalId, metric, metricLabel, secondaryMetric, secondaryMetricLabel, selectedDate }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<Period>("1m");

  const { data, isLoading, error } = useQuery<TrendlineResponse>({
    queryKey: ["trendline", terminalId, metric, selectedPeriod],
    queryFn: () => fetchTrendline(terminalId, metric, selectedPeriod),
    enabled: isOpen && !!terminalId && !!metric,
    retry: 1
  });

  const { data: secondaryData, isLoading: secondaryLoading, error: secondaryError } = useQuery<TrendlineResponse>({
    queryKey: ["trendline", terminalId, secondaryMetric, selectedPeriod],
    queryFn: () => fetchTrendline(terminalId, secondaryMetric!, selectedPeriod),
    enabled: isOpen && !!terminalId && !!secondaryMetric,
    retry: 1
  });

  // Filter data points based on selectedDate if it's not today
  const filterDataPoints = (trendline: TrendlineDataPoint[]) => {
    if (!selectedDate) return trendline;
    
    const selectedMoment = moment(selectedDate, 'MM/DD/YYYY');
    const todayMoment = moment();
    
    // Only filter if selected date is not today
    if (selectedMoment.isSame(todayMoment, 'day')) {
      return trendline;
    }
    
    // Filter out data points after the selected date
    return trendline.filter(point => {
      const pointDate = moment(point.date);
      return pointDate.isSameOrBefore(selectedMoment, 'day');
    });
  };

  const filteredTrendline = data ? filterDataPoints(data.trendline) : [];
  const filteredSecondaryTrendline = secondaryData ? filterDataPoints(secondaryData.trendline) : [];

  const chartData = data ? {
    labels: filteredTrendline.map(point => moment(point.date).format('MMM DD')),
    datasets: [
      {
        label: metricLabel,
        data: filteredTrendline.map(point => point.value),
        borderColor: '#4285F4',
        backgroundColor: 'rgba(66, 133, 244, 0.2)',
        tension: 0.4,
      },
      ...(secondaryData ? [{
        label: secondaryMetricLabel,
        data: filteredSecondaryTrendline.map(point => point.value),
        borderColor: '#34A853',
        backgroundColor: 'rgba(52, 168, 83, 0.2)',
        tension: 0.4,
      }] : []),
    ],
  } : null;

  const isPercentageMetric = metric.includes('perc') || metric.includes('percentage');
  
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: `${metricLabel} Trend`,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              if (isPercentageMetric) {
                label += context.parsed.y.toFixed(2) + '%';
              } else if (metric.includes('revenue')) {
                label += '$' + context.parsed.y.toFixed(2);
              } else {
                label += context.parsed.y.toFixed(0);
              }
            }
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: function (value: any) {
            if (isPercentageMetric) {
              return value + '%';
            } else if (metric.includes('revenue')) {
              return '$' + value.toFixed(0);
            } else {
              return value;
            }
          },
        },
      },
    },
  };

  const periods: { value: Period; label: string }[] = [
    { value: "1w", label: "1 Week" },
    { value: "1m", label: "1 Month" },
    { value: "6m", label: "6 Months" },
  ];

  return (
    <SlideOutSidebar
      show={isOpen}
      onClose={onClose}
      header={`${metricLabel} Trend`}
    >
      <div className="trendline-modal">
        <div className="trendline-modal__period-selector">
          {periods.map((period) => (
            <button
              key={period.value}
              className={`trendline-modal__period-button ${selectedPeriod === period.value ? 'active' : ''}`}
              onClick={() => setSelectedPeriod(period.value)}
            >
              {period.label}
            </button>
          ))}
        </div>

        <div className="trendline-modal__content">
          {(isLoading || (secondaryMetric && secondaryLoading)) && (
            <div className="trendline-modal__loading">
              <Loading isLoading />
            </div>
          )}

          {(error || (secondaryMetric && secondaryError)) && (
            <div className="trendline-modal__error">
              Failed to load trend data. Please try again.
            </div>
          )}

          {data && chartData && (!secondaryMetric || secondaryData) && (
            <>
              <div className="trendline-modal__stats" style={{ gridTemplateColumns: secondaryData ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)' }}>
                <div className="trendline-modal__stat">
                  <span className="trendline-modal__stat-label">Period</span>
                  <span className="trendline-modal__stat-value">
                    {(() => {
                      const startDate = moment(data.start_date);
                      let endDate = moment(data.end_date);
                      
                      // If selectedDate is provided and not today, use it as the end date
                      if (selectedDate) {
                        const selectedMoment = moment(selectedDate, 'MM/DD/YYYY');
                        const todayMoment = moment();
                        if (!selectedMoment.isSame(todayMoment, 'day') && filteredTrendline.length > 0) {
                          // Use the last filtered data point's date as the end date
                          const lastPointDate = moment(filteredTrendline[filteredTrendline.length - 1].date);
                          endDate = lastPointDate.isBefore(selectedMoment) ? lastPointDate : selectedMoment;
                        }
                      }
                      
                      return `${startDate.format('MMM DD, YYYY')} - ${endDate.format('MMM DD, YYYY')}`;
                    })()}
                  </span>
                </div>
                <div className="trendline-modal__stat">
                  <span className="trendline-modal__stat-label">Data Points</span>
                  <span className="trendline-modal__stat-value">{filteredTrendline.length}</span>
                </div>
                <div className="trendline-modal__stat">
                  <span className="trendline-modal__stat-label">Avg {metricLabel}</span>
                  <span className="trendline-modal__stat-value">
                    {(() => {
                      if (filteredTrendline.length === 0) return 'N/A';
                      const average = filteredTrendline.reduce((sum, point) => sum + point.value, 0) / filteredTrendline.length;
                      if (isPercentageMetric) {
                        return average.toFixed(2) + '%';
                      } else if (metric.includes('revenue')) {
                        return '$' + average.toFixed(2);
                      } else {
                        return average.toFixed(0);
                      }
                    })()}
                  </span>
                </div>
                {secondaryData && (
                  <div className="trendline-modal__stat">
                    <span className="trendline-modal__stat-label">Avg {secondaryMetricLabel}</span>
                    <span className="trendline-modal__stat-value">
                      {(() => {
                        if (filteredSecondaryTrendline.length === 0) return 'N/A';
                        const average = filteredSecondaryTrendline.reduce((sum, point) => sum + point.value, 0) / filteredSecondaryTrendline.length;
                        if (isPercentageMetric) {
                          return average.toFixed(2) + '%';
                        } else if (metric.includes('revenue')) {
                          return '$' + average.toFixed(2);
                        } else {
                          return average.toFixed(0);
                        }
                      })()}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="trendline-modal__chart">
                <Line data={chartData} options={chartOptions} />
              </div>
            </>
          )}
        </div>
      </div>
    </SlideOutSidebar>
  );
};

export default TrendlineModal;

